import { MenuButtonWebElementBase } from './MenuButtonWebElementBase';
import { By } from 'selenium-webdriver';

const MENU_ITEM_LOCATION =
  '#__oj_zorder_container [role=menuitem], #__root_layer_host [role=menuitem], ' +
  '#__oj_zorder_container [role=menuitemradio], #__root_layer_host [role=menuitemradio], ' +
  '#__oj_zorder_container [role=menuitemcheckbox], #__root_layer_host [role=menuitemcheckbox]';

/**
 * The component WebElement for [oj-c-menu-button](../../jsdocs/oj-c.MenuButton.html).
 * Do not instantiate this class directly, instead, use
 * [findMenuButton](../functions/findMenuButton.html).
 */
export class MenuButtonWebElement extends MenuButtonWebElementBase {
  /**
   * Perform a click on the button to open the menu or close the menu
   */
  async click() {
    // Find the <button> element to click so that it can receive focus
    const button = await this.findElement(By.css('button'));
    return button.click();
  }

  /**
   * Helper util
   * */
  async findAsyncSequential<T>(
    array: T[],
    predicate: (t: T) => Promise<boolean>
  ): Promise<T | undefined> {
    for (const t of array) {
      if (await predicate(t)) {
        return t;
      }
    }
    return undefined;
  }

  /**
   * Fire the ojMenuAction event on the oj-c-menu-button, and
   * invoke the Action handler of the selected value.
   *
   */
  public async doMenuAction(selectedValue: string | string[]): Promise<void> {
    // open the menu
    await this.openMenu();
    if (Array.isArray(selectedValue)) {
      for (const value of selectedValue) {
        await this.doMenuClick(value);
      }
    } else {
      await this.doMenuClick(selectedValue);
    }
  }

  /**
   * openMenu - opens the menu
   */
  private async openMenu() {
    const button = await this.findElement(By.css('button[type=button]'));
    await button.click();
  }

  /**
   * delay - delays the milliseconds
   * await delay(2000);
   */
  private delay = (ms: number) => new Promise((fn) => setTimeout(fn, ms));

  /**
   * In order to do nested selections, we need an isolated menu clicker
   */
  private async doMenuClick(selectedValue: string): Promise<void> {
    // Find the <menu-item> element to click
    await this.delay(350);
    const menuItems = await this.getDriver().findElements(By.css(MENU_ITEM_LOCATION));

    const match = await this.findAsyncSequential(menuItems, async (item) => {
      const text = await item.getText();
      return text === selectedValue;
    });

    await match?.click();
  }
}
